/**
 * E2E message/reaction signature preimage construction (v2).
 *
 * v1 signatures covered only `domain || ciphertext || nonce || JSON(wrappedKeys)`,
 * which left messages unbound to their conversation, sender, and client message
 * identity. A malicious server could replay a validly signed message into a
 * different conversation between the same participants.
 *
 * v2 binds the full message context into the signed preimage:
 *   - conversationId (24-hex)
 *   - fromIdentityId (24-hex)
 *   - clientMessageId / clientReactionId (UUID)
 *   - messageId for reactions (24-hex)
 *
 * The preimage is a single UTF-8 string with `\n` separators. None of the
 * bound fields can contain a newline (hex IDs, UUIDs, base64), and the
 * wrapped-keys JSON is the final component, so the encoding is unambiguous.
 *
 * Wrapped keys are serialized with an explicit canonical key order so that
 * client-side signing and server-side verification produce byte-identical
 * JSON regardless of intermediate parsing (e.g. zod re-ordering object keys).
 *
 * SECURITY: both signer and verifier MUST build the preimage through these
 * helpers. Any drift between the two sides silently breaks verification.
 *
 * @module messaging/signatureBinding
 */
/** Signing domain for v1 message signatures (legacy, context-unbound). */
export const MESSAGE_SIGN_DOMAIN_V1 = 'adieuu-msg-v1';
/** Signing domain for v2 message signatures (context-bound). */
export const MESSAGE_SIGN_DOMAIN_V2 = 'adieuu-msg-v2';
/** Signing domain for v1 reaction signatures (legacy, context-unbound). */
export const REACTION_SIGN_DOMAIN_V1 = 'adieuu-reaction-v1';
/** Signing domain for v2 reaction signatures (context-bound). */
export const REACTION_SIGN_DOMAIN_V2 = 'adieuu-reaction-v2';
/** Signing domain for Space channel message signatures (context-bound). */
export const SPACE_MESSAGE_SIGN_DOMAIN_V1 = 'adieuu-space-msg-v1';
/** Signing domain for Space channel reaction HMAC tags (context-bound). */
export const SPACE_REACTION_SIGN_DOMAIN_V1 = 'adieuu-space-reaction-v1';
/** Signing domain for Space announcement signatures (context-bound). */
export const SPACE_ANNOUNCEMENT_SIGN_DOMAIN_V1 = 'adieuu-space-announcement-v1';
/**
 * Canonical wrapped-key serialization for signature preimages.
 *
 * Emits keys in a fixed order and omits absent optional fields, matching
 * what `JSON.stringify` produces for the objects built at encrypt time.
 * This guards against key re-ordering by intermediate parsers (zod strips
 * and re-orders object keys on the server).
 */
export function serializeWrappedKeysForSignature(wrappedKeys) {
    const canonical = wrappedKeys.map((wk) => {
        const out = {
            identityId: wk.identityId,
            ephemeralPublicKey: wk.ephemeralPublicKey,
            kemCiphertext: wk.kemCiphertext,
            wrappedSessionKey: wk.wrappedSessionKey,
            wrappingNonce: wk.wrappingNonce,
            preKeyType: wk.preKeyType,
        };
        if (wk.signedPreKeyId != null)
            out.signedPreKeyId = wk.signedPreKeyId;
        if (wk.oneTimePreKeyId != null)
            out.oneTimePreKeyId = wk.oneTimePreKeyId;
        if (wk.spkKemCiphertext != null)
            out.spkKemCiphertext = wk.spkKemCiphertext;
        if (wk.otpkKemCiphertext != null)
            out.otpkKemCiphertext = wk.otpkKemCiphertext;
        if (wk.routingTag != null)
            out.routingTag = wk.routingTag;
        if (wk.wrapVersion != null)
            out.wrapVersion = wk.wrapVersion;
        return out;
    });
    return JSON.stringify(canonical);
}
/**
 * Builds the v2 message signature preimage string.
 *
 * Layout: `domain \n conversationId \n fromIdentityId \n clientMessageId \n
 * ciphertextB64 \n nonceB64 \n canonicalWrappedKeysJson`
 */
export function buildMessageSignaturePreimageV2(context, ciphertextB64, nonceB64, wrappedKeys) {
    return [
        MESSAGE_SIGN_DOMAIN_V2,
        context.conversationId,
        context.fromIdentityId,
        context.clientMessageId,
        ciphertextB64,
        nonceB64,
        serializeWrappedKeysForSignature(wrappedKeys),
    ].join('\n');
}
/**
 * Builds the Space message signature preimage string.
 *
 * Layout: `domain \n spaceId \n channelId \n fromIdentityId \n
 * clientMessageId \n ciphertextB64 \n nonceB64 \n cipherId`
 *
 * cipherId is a hex digest and the other variable fields are base64 / hex /
 * UUID, so none contain newlines.
 */
export function buildSpaceMessageSignaturePreimage(context, ciphertextB64, nonceB64, cipherId) {
    return [
        SPACE_MESSAGE_SIGN_DOMAIN_V1,
        context.spaceId,
        context.channelId,
        context.fromIdentityId,
        context.clientMessageId,
        ciphertextB64,
        nonceB64,
        cipherId,
    ].join('\n');
}
/**
 * Builds the Space announcement signature preimage string.
 *
 * Layout: `domain \n spaceId \n clientAnnouncementId \n signerIdentityId \n
 * revision \n match \n channelIds \n roleIds \n ciphertextB64 \n nonceB64 \n
 * cipherId`
 *
 * Audience id lists are sorted and comma-joined so the encoding does not
 * depend on selection order. Ids are 24-hex, so neither commas nor newlines
 * can appear inside a field.
 */
export function buildSpaceAnnouncementSignaturePreimage(context, ciphertextB64, nonceB64, cipherId) {
    return [
        SPACE_ANNOUNCEMENT_SIGN_DOMAIN_V1,
        context.spaceId,
        context.clientAnnouncementId,
        context.signerIdentityId,
        String(context.revision),
        context.audience.match,
        [...context.audience.channelIds].sort().join(','),
        [...context.audience.roleIds].sort().join(','),
        ciphertextB64,
        nonceB64,
        cipherId,
    ].join('\n');
}
/**
 * Builds the Space reaction signature preimage string.
 *
 * Layout: `domain \n spaceId \n channelId \n messageId \n fromIdentityId \n tag`
 *
 * `tag` is a lowercase 64-char hex HMAC digest. The other bound fields are
 * 24-hex ObjectIds, so none contain newlines.
 */
export function buildSpaceReactionSignaturePreimage(context, tag) {
    return [
        SPACE_REACTION_SIGN_DOMAIN_V1,
        context.spaceId,
        context.channelId,
        context.messageId,
        context.fromIdentityId,
        tag,
    ].join('\n');
}
/**
 * Builds the v2 reaction signature preimage string.
 *
 * Layout: `domain \n conversationId \n messageId \n fromIdentityId \n
 * clientReactionId \n ciphertextB64 \n nonceB64 \n canonicalWrappedKeysJson`
 */
export function buildReactionSignaturePreimageV2(context, ciphertextB64, nonceB64, wrappedKeys) {
    return [
        REACTION_SIGN_DOMAIN_V2,
        context.conversationId,
        context.messageId,
        context.fromIdentityId,
        context.clientReactionId,
        ciphertextB64,
        nonceB64,
        serializeWrappedKeysForSignature(wrappedKeys),
    ].join('\n');
}
//# sourceMappingURL=signatureBinding.js.map